/**
 * src/lib/constants/categories.js — Canonical material-category color maps.
 *
 * Single source of truth for material category styling.
 * Uses CSS custom-property tokens where possible; HEX exceptions are
 * documented in color-map.json.
 */

/**
 * Fallback product categories — used when the DB lookup table is empty.
 * 2026-04-26 (B5c): trimmed to AC Nautitech's actual service set per CEO.
 * Mirrors the active rows in product_categories. "Other" is the catch-all
 * trigger — when picked, the form prompts for a suggested name; an empty
 * suggestion falls into the "General" bucket.
 * All files should reference this instead of inline arrays.
 */
export const FALLBACK_PRODUCT_CATEGORIES = ['Bimini', 'Upholstery', 'Canvas & Covers', 'Spray hood', 'Teak', 'Other'];

/**
 * Canonical MATERIAL categories. `value` MUST mirror the materials_catalog.category
 * CHECK constraint exactly — the DB only accepts these lowercase slugs
 * (fabric|metal|hardware|paint|wood|clear_pvc|other) and rejects anything else
 * (e.g. Title-case 'Other' or a stray 'Paint'). The UI must submit/filter on
 * `value`; `label` is display-only. Single source of truth for the catalog
 * category select + filter chips.
 */
export const MATERIAL_CATEGORIES = [
  { value: 'fabric', label: 'Fabric' },
  { value: 'metal', label: 'Metal' },
  { value: 'hardware', label: 'Hardware' },
  { value: 'paint', label: 'Paint' },
  { value: 'wood', label: 'Wood' },
  { value: 'clear_pvc', label: 'Clear PVC' },
  { value: 'other', label: 'Other' },
];

/** slug -> display label, for rendering a stored category value. */
export const MATERIAL_CATEGORY_LABELS = Object.fromEntries(
  MATERIAL_CATEGORIES.map((c) => [c.value, c.label]),
);

/**
 * Flat accent color per material category (lowercase slug keys).
 * Used for borders, dots, and text highlights.
 */
export const MATERIAL_CATEGORY_COLORS = {
  // HEX_TODO[#3B82F6] exception — see color-map.json
  fabric:    '#3B82F6',
  metal:     'var(--tm)',
  hardware:  'var(--warn)',
  paint:     'var(--bad)',
  wood:      'var(--ok)',
  // HEX_TODO[#8B5CF6] exception — see color-map.json
  clear_pvc: '#8B5CF6',
  other:     'var(--tm)',
};

/**
 * Badge-style bg + color pairs for material categories. Keyed by the lowercase
 * DB slug (materials_catalog.category) so a stored value looks up directly —
 * was Title-case before, which never matched the lowercase DB value and made
 * every badge fall back to the 'other' colour. HEX_TODO bg tints — see color-map.json.
 */
export const MATERIAL_CATEGORY_BADGE_STYLES = {
  fabric:    { bg: '#7C3AED18', color: 'var(--accent)' },
  metal:     { bg: '#6B728018', color: 'var(--tm)' },
  hardware:  { bg: '#F5920B18', color: 'var(--warn)' },
  paint:     { bg: '#3B82F618', color: 'var(--info)' },
  wood:      { bg: '#92400E18', color: 'var(--warn)' },
  clear_pvc: { bg: '#8B5CF618', color: 'var(--accent)' },
  other:     { bg: '#06B6D418', color: 'var(--info)' },
};
